import { useReportClock } from '../reportClock';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { AlertTriangle, Bell, Download, FileText, Search } from 'react-feather';
import { useLocation } from 'react-router-dom';
import {
  Badge,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Row,
} from 'reactstrap';
import { Breadcrumbs, H2, H6, P } from '../../../AbstractElements';
import i18n from '../../../i18n';
import { loadVehicleNoticeAlerts, VEHICLE_NOTICE_ALERT_THRESHOLD } from './vehicleAlertsData';
import AvisDetailModal from '../Avis/AvisDetailModal';

const paginationComponentOptions = {
  rowsPerPageText: 'Alertes par page',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
  selectAllRowsItemText: 'Toutes',
};

const getInitialPlateFilter = search => new URLSearchParams(search || '').get('plate') || '';

const escapeCsv = value => `"${String(value || '').replace(/"/g, '""')}"`;

const downloadAlertsCsv = alerts => {
  const headers = [
    'Plaque',
    'Nombre avis',
    'Dernier avis',
    'Derniere date',
    'Vehicule',
    'Lieu',
    'Agent',
  ];
  const rows = alerts.map(alert => [
    alert.plate,
    alert.noticeCount,
    alert.latestTicketId,
    alert.latestDateLabel,
    alert.vehicleDescription,
    alert.location,
    alert.agent,
  ]);
  const csvContent = [headers, ...rows]
    .map(row => row.map(escapeCsv).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', `alertes-vehicules-${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const AlertMetricCard = ({ icon, title, value, subtitle, color = 'primary' }) => (
  <Card>
    <CardBody>
      <div className="media">
        <div className={`media-static bg-light-${color} text-${color} me-3`}>
          {icon}
        </div>
        <div className="media-body">
          <H6 attrH6={{ className: 'mb-1' }}>{title}</H6>
          <H2 attrH6={{ className: 'mb-0' }}>{value}</H2>
          {subtitle && <P attrPara={{ className: 'mb-0 text-muted' }}>{subtitle}</P>}
        </div>
      </div>
    </CardBody>
  </Card>
);

const VehicleAlertLevel = ({ level }) => (
  <Badge color={level === 'critical' ? 'danger' : 'warning'}>
    {level === 'critical' ? 'Critique' : 'Avertissement'}
  </Badge>
);

const AvisModalButton = ({ avisId, children, className = 'btn btn-link p-0 font-primary', onOpenAvis }) => (
  <button
    type="button"
    className={className}
    onClick={() => onOpenAvis(avisId)}
    disabled={!avisId}
    aria-label={`Ouvrir l'avis ${avisId} dans une fenetre modale`}
  >
    {children}
  </button>
);

const RecentTickets = ({ data, onOpenAvis }) => (
  <div className="p-3">
    <H6 attrH6={{ className: 'mb-2' }}>Avis recents pour {data.plate}</H6>
    <div className="table-responsive">
      <table className="table table-sm mb-0">
        <thead>
          <tr>
            <th>Avis</th>
            <th>Date</th>
            <th>Lieu</th>
            <th>Vehicule</th>
          </tr>
        </thead>
        <tbody>
          {data.tickets.map(ticket => (
            <tr key={ticket.ticketid || ticket.ticketId || `${data.plate}-${ticket.datetime}`}>
              <td>
                {ticket.ticketid || ticket.ticketId ? (
                  <AvisModalButton avisId={ticket.ticketid || ticket.ticketId} onOpenAvis={onOpenAvis}>
                    {ticket.ticketid || ticket.ticketId}
                  </AvisModalButton>
                ) : '-'}
              </td>
              <td>{ticket.datetime || [ticket.date, ticket.time].filter(Boolean).join(' ') || '-'}</td>
              <td>{ticket.location || ticket.address || ticket.contract || '-'}</td>
              <td>{[ticket.carbrand, ticket.carmodel, ticket.carcolor].filter(Boolean).join(' ') || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const VehicleAlertsPage = () => {
  const reportNow = useReportClock();
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => getInitialPlateFilter(location.search));
  const [selectedAvisId, setSelectedAvisId] = useState('');

  useEffect(() => {
    setSearch(getInitialPlateFilter(location.search));
  }, [location.search]);

  useEffect(() => {
    let active = true;

    const loadAlerts = async () => {

      setError('');

      try {
        const result = await loadVehicleNoticeAlerts();

        if (active) {
          setAlerts(result.alerts);
          setMetadata(result);
        }
      } catch (loadError) {
        if (active) {
          setAlerts([]);
          setMetadata(null);
          setError(loadError.message || 'Impossible de charger les alertes vehicules.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadAlerts();

    return () => {
      active = false;
    };
  }, [reportNow]);

  const visibleAlerts = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return alerts;
    }

    return alerts.filter(alert => [
      alert.plate,
      alert.plateNormalized,
      alert.vehicleDescription,
      alert.location,
      alert.agent,
      alert.latestTicketId,
    ].some(value => String(value || '').toLowerCase().includes(query)));
  }, [alerts, search]);

  const highestCount = alerts.reduce((highest, alert) => Math.max(highest, alert.noticeCount), 0);
  const latestAlert = alerts.reduce((latest, alert) => (
    alert.latestTimestamp > (latest?.latestTimestamp || 0) ? alert : latest
  ), null);
  const partialLoadWarnings = metadata?.errors?.length ? metadata.errors.length : 0;

  const tableColumns = [
    {
      name: 'Niveau',
      selector: row => <VehicleAlertLevel level={row.level} />,
      sortable: true,
      width: '130px',
    },
    {
      name: 'Plaque',
      selector: row => (
        <div>
          <strong>{row.plate}</strong>
          <div className="text-muted">{row.plateNormalized}</div>
        </div>
      ),
      sortable: true,
    },
    {
      name: 'Avis',
      selector: row => row.noticeCount,
      sortable: true,
      center: true,
      width: '95px',
    },
    {
      name: 'Vehicule',
      selector: row => row.vehicleDescription || '-',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Lieu / contrat',
      selector: row => row.location || '-',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Dernier avis',
      selector: row => (
        row.latestTicketId ? (
          <AvisModalButton avisId={row.latestTicketId} onOpenAvis={setSelectedAvisId}>
            {row.latestTicketId}
          </AvisModalButton>
        ) : '-'
      ),
      sortable: true,
    },
    {
      name: 'Date',
      selector: row => row.latestDateLabel || '-',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Action',
      selector: row => (
        <AvisModalButton
          avisId={row.latestTicketId}
          onOpenAvis={setSelectedAvisId}
          className="btn btn-outline-primary btn-sm"
        >
          Voir avis
        </AvisModalButton>
      ),
      width: '125px',
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={i18n.t('Dashboard_Common_Admin')}
        parent={i18n.t('Dashboard_Common_Dashboard')}
        title="Alertes vehicules"
      />
      <Row>
        <Col xl="4" md="6">
          <AlertMetricCard
            icon={<AlertTriangle />}
            title="Vehicules a surveiller"
            value={loading ? '...' : alerts.length}
            subtitle={`${VEHICLE_NOTICE_ALERT_THRESHOLD} avis ou plus`}
            color="danger"
          />
        </Col>
        <Col xl="4" md="6">
          <AlertMetricCard
            icon={<Bell />}
            title="Plus grand cumul"
            value={loading ? '...' : highestCount}
            subtitle="avis sur une meme plaque"
            color="warning"
          />
        </Col>
        <Col xl="4" md="12">
          <AlertMetricCard
            icon={<FileText />}
            title="Derniere alerte"
            value={loading ? '...' : latestAlert?.plate || '-'}
            subtitle={latestAlert?.latestDateLabel || 'Aucun avis recent'}
            color="primary"
          />
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <Row className="align-items-start">
                <Col lg="7">
                  <H2 attrH6={{ className: 'title' }}>Alertes vehicules</H2>
                  <P>
                    Ce rapport affiche les plaques qui ont atteint {VEHICLE_NOTICE_ALERT_THRESHOLD} avis ou plus,
                    pour que l'equipe puisse intervenir meme si l'information n'a pas ete signalee sur le terrain.
                  </P>
                  {metadata?.loadedSources?.length > 0 && (
                    <P attrPara={{ className: 'text-muted mb-0' }}>
                      Sources lues: {metadata.loadedSources.join(', ')}.
                      {partialLoadWarnings > 0 && ` ${partialLoadWarnings} source(s) partielle(s).`}
                    </P>
                  )}
                </Col>
                <Col lg="5">
                  <Row>
                    <Col md="7">
                      <FormGroup>
                        <InputGroup className="pill-input-group">
                          <InputGroupText><Search size={16} /></InputGroupText>
                          <Input
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            placeholder="Filtrer par plaque, lieu, agent..."
                          />
                        </InputGroup>
                      </FormGroup>
                    </Col>
                    <Col md="5">
                      <button
                        type="button"
                        className="btn btn-secondary btn-block"
                        disabled={!visibleAlerts.length}
                        onClick={() => downloadAlertsCsv(visibleAlerts)}
                      >
                        <Download className="h-15" /> Export CSV
                      </button>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <DataTable
                className="table-avis-list m-t-20"
                data={visibleAlerts}
                columns={tableColumns}
                striped
                noHeader
                progressPending={loading}
                noDataComponent={loading ? 'Chargement des alertes...' : 'Aucun vehicule a 5 avis ou plus.'}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                paginationComponentOptions={paginationComponentOptions}
                expandableRows
                expandableRowsComponent={RecentTickets}
                expandableRowsComponentProps={{ onOpenAvis: setSelectedAvisId }}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <AvisDetailModal
        avisId={selectedAvisId}
        isOpen={Boolean(selectedAvisId)}
        toggle={() => setSelectedAvisId('')}
      />
    </Fragment>
  );
};

export default VehicleAlertsPage;
